import { Suspense, lazy, useState } from "react";
import { FileDown } from "lucide-react";
import { api, downloadFile } from "../lib/api";
import { useFetch } from "../lib/useFetch";
// SurveyJS is ~1 MB; only the people filling in a plan should pay for it,
// the same bargain the Stream SDK gets in App.tsx.
const SurveyForm = lazy(() => import("./SurveyForm"));

/**
 * Care plans and risk assessments (FR-PRT-03): the same record in two flavours
 * — versioned draft → active → superseded, filled in against the org's
 * template or as free text, exportable as a PDF.
 *
 * One component for both because they differ only in wording and endpoint; a
 * second copy would be a second place to fix the version history.
 */

export type FormKind = "care_plan" | "risk_assessment";

interface Record_ {
  id: number; version: number; status: string; content: string;
  answers: Record<string, unknown> | null;
  templateId: number | null;
  riskLevel?: string | null;
  reviewDate: string | null; createdAt: string;
}
interface Template { id: number; kind: string; name: string; version: number; schema: unknown }

const CONFIG: Record<FormKind, {
  title: string; resource: string; fileStem: string; empty: string; placeholder: string;
}> = {
  care_plan: {
    title: "Care plan",
    resource: "care-plans",
    fileStem: "care-plan",
    empty: "No care plan yet — create the first version.",
    placeholder: "Support needs, goals linkage, frequency of services, risk considerations…",
  },
  risk_assessment: {
    title: "Risk assessment",
    resource: "risk-assessments",
    fileStem: "risk-assessment",
    empty: "No risk assessment yet. Onboarding is blocked until there is a current one.",
    placeholder: "Identified risks, likelihood and consequence, controls, escalation…",
  },
};

const statusBadge: Record<string, string> = {
  active: "bg-teal-50 text-teal-700",
  draft: "bg-amber-50 text-amber-700",
  superseded: "bg-slate-100 text-faint",
};
const riskBadge: Record<string, string> = {
  low: "bg-teal-50 text-teal-700",
  medium: "bg-amber-50 text-amber-700",
  high: "bg-orange-100 text-orange-800",
  extreme: "bg-red-100 text-red-700",
};
const fmt = (iso: string) => new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });

export default function VersionedFormSection({
  kind, participantId, participantName,
}: { kind: FormKind; participantId: string; participantName: string }) {
  const config = CONFIG[kind];
  const records = useFetch<Record_[]>(`/v1/participants/${participantId}/${config.resource}`);
  // Loaded up front so the form appears without a second wait, and so an org
  // with a customised template never sees the built-in questions by mistake.
  const template = useFetch<Template[]>(`/v1/form-templates?kind=${kind}`);

  const [mode, setMode] = useState<"closed" | "form" | "text">("closed");
  const [text, setText] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  if (records.error) return null; // no permission to see these

  async function create(payload: { content?: string; answers?: Record<string, unknown> }) {
    setError(null);
    try {
      await api(`/v1/participants/${participantId}/${config.resource}`, {
        method: "POST",
        body: JSON.stringify({ ...payload, ...(reviewDate ? { reviewDate } : {}) }),
      });
      setMode("closed"); setText(""); setReviewDate("");
      records.reload();
    } catch (e) { setError((e as Error).message); }
  }

  async function activate(id: number) {
    setError(null);
    try {
      await api(`/v1/${config.resource}/${id}/activate`, { method: "POST", body: "{}" });
      records.reload();
    } catch (e) { setError((e as Error).message); }
  }

  async function exportPdf(record: Record_) {
    setError(null);
    const stem = participantName.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "");
    try {
      await downloadFile(`/v1/${config.resource}/${record.id}/export.pdf`,
        `${config.fileStem}-${stem}-v${record.version}.pdf`);
    } catch (e) { setError((e as Error).message); }
  }

  const active = template.data?.[0];
  const schema = active?.schema;
  /**
   * Only a record answered against the *current* template can be replayed
   * through the form — an older one would silently lose any question since
   * removed. Its server-rendered text was produced against the template it was
   * actually answered on, so that stays the honest view (and the PDF does the
   * same, from the archived version).
   */
  const replayable = (record: Record_) => Boolean(record.answers && active && record.templateId === active.id);

  return (
    <section className="card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-ink">{config.title}</h2>
        <div className="flex gap-2">
          {mode === "closed" ? (
            <>
              <button onClick={() => setMode("form")} disabled={!schema} className="btn-primary px-3 py-1.5">
                New version
              </button>
              <button onClick={() => setMode("text")} className="btn-secondary px-3 py-1.5">
                Free text
              </button>
            </>
          ) : (
            <button onClick={() => { setMode("closed"); setError(null); }} className="btn-secondary px-3 py-1.5">
              Cancel
            </button>
          )}
        </div>
      </div>

      {mode !== "closed" && (
        <label className="block max-w-xs">
          <span className="label">Review date (optional)</span>
          <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className="input" />
        </label>
      )}

      {mode === "form" && schema !== undefined && (
        <div className="border border-edge-soft rounded-xl overflow-hidden">
          <Suspense fallback={<p className="p-4 text-sm text-mut">Loading the form…</p>}>
            <SurveyForm schema={schema} submitText="Save draft"
              onSubmit={(answers) => create({ answers })} />
          </Suspense>
        </div>
      )}

      {mode === "text" && (
        <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); void create({ content: text }); }}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
            placeholder={config.placeholder} className="input mt-0"
            aria-label={`${config.title} text`} />
          <button className="btn-dark">Save draft</button>
        </form>
      )}

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {records.data?.length === 0 && mode === "closed" && (
        <p className="text-sm text-faint">{config.empty}</p>
      )}

      <div className="space-y-3">
        {records.data?.map((record) => (
          <div key={record.id} className="border border-edge-soft rounded-xl p-4">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink">Version {record.version}</span>
                <span className={`chip ${statusBadge[record.status] ?? ""}`}>{record.status}</span>
                {record.riskLevel && (
                  <span className={`chip ${riskBadge[record.riskLevel] ?? ""}`}>{record.riskLevel} risk</span>
                )}
                <span className="text-xs text-faint">{fmt(record.createdAt)}</span>
                {record.reviewDate && <span className="text-xs text-faint">· review {record.reviewDate}</span>}
              </div>
              <div className="flex items-center gap-2">
                {replayable(record) && (
                  <button onClick={() => setExpanded(expanded === record.id ? null : record.id)}
                    className="btn-secondary px-3 py-1.5 text-xs">
                    {expanded === record.id ? "Show as text" : "Show as form"}
                  </button>
                )}
                <button onClick={() => exportPdf(record)}
                  aria-label={`Download ${config.title.toLowerCase()} version ${record.version} as PDF`}
                  className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5">
                  <FileDown className="h-3.5 w-3.5" aria-hidden="true" />
                  PDF
                </button>
                {record.status === "draft" && (
                  <button onClick={() => activate(record.id)} className="btn-primary px-3 py-1.5 text-xs">
                    Activate
                  </button>
                )}
              </div>
            </div>
            {expanded === record.id && replayable(record) ? (
              <Suspense fallback={<p className="text-sm text-mut">Loading the form…</p>}>
                <SurveyForm schema={schema} answers={record.answers} readOnly />
              </Suspense>
            ) : (
              <p className="text-sm text-body whitespace-pre-wrap">{record.content}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
